// TODO: Scripted copied from Retool custom component, refactor it later
export interface PatientDetails {
  statement: {
    patient: {
      id: string
      externalId: string
      first: string
      middle: string | null
      last: string
      suffix: string | null
      addresses: Array<{
        lines: string[]
        city: string
        state: string
        postal: string
        country: string
      }>
      dob: string
      sex: string
      phones: string[]
      emails: string[]
    }
    billingProvider: {
      organization: string
      addresses: Array<{
        lines: string[]
        city: string
        state: string
        postal: string
        country: string
      }>
      email: string
      phone: string
      npi?: string
      ein?: string
    }
    balanceDue: {
      amount: string
      currency: string
    }
  }
  paymentsList: Array<{
    id: string
    totalAmount: { amount: string; currency: string }
    unallocatedAmount: { amount: string; currency: string }
    allocatedAmount: { amount: string; currency: string }
    externalTransactionId: string
    paymentDate: string
    refundAmount: { amount: string; currency: string }
  }>
  statementsList: Array<{
    id: string
    type: string
    date: string
    description: string
    charge: string
    credit: string
    balance: string
    diagnosisCodes?: string[]
    renderingProviders?: Array<{
      firstName: string
      lastName: string
      npi?: string
    }>
    facility?: string
    billingNpi?: string
    renderingNpi?: string
    ein?: string
    metadata?: any
  }>
  claimsList?: Array<{
    id: string
    claimResponsibilityType?: string
    fromDos?: string
    throughDos?: string
    patientFirst?: string
    patientLast?: string
    primaryPayerName?: string
    chargeAmount?: { amount: string; currency: string }
  }>
}

// Function to escape HTML entities to prevent XSS
const escapeHtml = (text: string | undefined | null): string => {
  if (text === null || text === undefined) {
    return ''
  }
  const map: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  }
  return String(text).replace(/[&<>"']/g, (m) => map[m])
}

// Function to format currency
export const formatCurrency = (amount: string, currency: string = 'USD') => {
  const num = parseFloat(amount)
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(num)
}

// Function to format date
export const formatDate = (dateString: string) => {
  // Parse the date string as local date to avoid timezone issues
  // Split the date string and create a local date object
  if (!dateString) {
    return '—'
  }
  
  try {
    const [year, month, day] = dateString.split('-').map(Number)
    const date = new Date(year, month - 1, day) // month is 0-indexed in Date constructor
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch (error) {
    return '—'
  }
}

// Function to generate payments table rows
const generatePaymentsTableRows = (paymentsList: PatientDetails['paymentsList']): string => {
  if (paymentsList?.length === 0) {
    return '<tr><td colspan="5" class="muted">No rows found</td></tr>'
  }

  // Sort payments by paymentDate (newest first), then by id for stability
  const sorted = [...paymentsList].sort((a, b) => {
    const aDate = a?.paymentDate || ''
    const bDate = b?.paymentDate || ''

    if (aDate && bDate && aDate !== bDate) {
      return new Date(bDate).getTime() - new Date(aDate).getTime()
    }

    const aId = a?.id || ''
    const bId = b?.id || ''
    return aId.localeCompare(bId)
  })

  let allRows = ''
  let rowIndex = 0
  let lastPaymentDate = ''

  sorted.forEach((payment, index) => {
    const currentPaymentDate = payment.paymentDate || ''
    const isNewDateGroup = currentPaymentDate !== lastPaymentDate
    
    // Add separator when payment date changes (except for first row)
    if (isNewDateGroup && index > 0) {
      allRows += `
        <tr class="date-separator">
          <td colspan="5" style="padding: 4px 0; border-top: 2px solid var(--line); background-color: #f8f9fa;"></td>
        </tr>
      `
    }
    
    const isAltRow = rowIndex % 2 === 1
    const rowClass = isAltRow ? 'row-alt' : ''
    
    allRows += `
      <tr class="${rowClass}">
        <td>${formatDate(payment.paymentDate)}</td>
        <td class="right">${formatCurrency(payment?.totalAmount?.amount, payment?.totalAmount?.currency)}</td>
        <td class="right">${formatCurrency(payment?.unallocatedAmount?.amount, payment?.unallocatedAmount?.currency)}</td>
        <td class="right">${formatCurrency(payment?.allocatedAmount?.amount, payment?.allocatedAmount?.currency)}</td>
        <td class="right">${formatCurrency(payment?.refundAmount?.amount, payment?.refundAmount?.currency)}</td>
      </tr>
    `
    
    lastPaymentDate = currentPaymentDate
    rowIndex++
  })

  // Calculate totals
  let totalUnallocated = 0
  let totalAllocated = 0
  let totalRefund = 0

  paymentsList.forEach(payment => {
    if (payment.unallocatedAmount && payment.unallocatedAmount.amount !== '—') {
      const unallocatedValue = typeof payment.unallocatedAmount.amount === 'string' ? payment.unallocatedAmount.amount : String(payment.unallocatedAmount.amount)
      totalUnallocated += parseFloat(unallocatedValue.replace(/[$,]/g, '')) || 0
    }
    if (payment.allocatedAmount && payment.allocatedAmount.amount !== '—') {
      const allocatedValue = typeof payment.allocatedAmount.amount === 'string' ? payment.allocatedAmount.amount : String(payment.allocatedAmount.amount)
      totalAllocated += parseFloat(allocatedValue.replace(/[$,]/g, '')) || 0
    }
    if (payment.refundAmount && payment.refundAmount.amount !== '—') {
      const refundValue = typeof payment.refundAmount.amount === 'string' ? payment.refundAmount.amount : String(payment.refundAmount.amount)
      totalRefund += parseFloat(refundValue.replace(/[$,]/g, '')) || 0
    }
  })

  // Add summary row
  const summaryRow = `
    <tr style="border-top: 2px solid var(--line); background-color: #f8f9fa; font-weight: bold;">
      <td colspan="2" style="text-align: right; padding: 12px 8px;">Total:</td>
      <td class="right" style="padding: 12px 8px;">${formatCurrency(totalUnallocated.toString())}</td>
      <td class="right" style="padding: 12px 8px;">${formatCurrency(totalAllocated.toString())}</td>
      <td class="right" style="padding: 12px 8px;">${formatCurrency(totalRefund.toString())}</td>
    </tr>
  `

  return allRows + summaryRow
}

// Function to format responsibility type
const formatResponsibility = (responsibilityType: string | undefined): string => {
  if (!responsibilityType) {
    return '—'
  }
  
  // Convert enum value to readable format (e.g., "ENTER" -> "Enter", "PAYER" -> "Payer")
  const formatted = responsibilityType
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
  return formatted || responsibilityType
}

// Function to format service dates
const formatServiceDates = (fromDos: string | undefined, throughDos: string | undefined): string => {
  const from = fromDos ? formatDate(fromDos) : null
  const through = throughDos ? formatDate(throughDos) : null
  
  if (from && through && from !== through) {
    return `${from} – ${through}`
  } else if (from) {
    return from
  } else {
    return '—'
  }
}

// Function to format patient name
const formatPatientName = (first: string | undefined, last: string | undefined): string => {
  if (first && last) {
    return `${first} ${last}`
  } else if (first) {
    return first
  } else if (last) {
    return last
  } else {
    return '—'
  }
}

// Function to generate claims table rows
const generateClaimsTableRows = (claimsList: PatientDetails['claimsList']): string => {
  if (!claimsList || claimsList.length === 0) {
    return '<tr><td colspan="6" class="muted">No rows found</td></tr>'
  }

  // Sort by fromDos (service start date); if equal or missing, fall back to id
  const sorted = [...claimsList].sort((a, b) => {
    const aDate = a?.fromDos || ''
    const bDate = b?.fromDos || ''

    if (aDate && bDate && aDate !== bDate) {
      // Newest first
      return new Date(bDate).getTime() - new Date(aDate).getTime()
    }

    const aId = a?.id || ''
    const bId = b?.id || ''
    return aId.localeCompare(bId)
  })

  let allRows = ''
  let rowIndex = 0
  let lastClaimId = ''

  sorted.forEach((claim, index) => {
    const currentClaimId = claim.id || ''
    const isNewClaimGroup = currentClaimId !== lastClaimId
    
    // Add separator when claim ID changes (except for first row)
    if (isNewClaimGroup && index > 0) {
      allRows += `
        <tr class="date-separator">
          <td colspan="6" style="padding: 4px 0; border-top: 2px solid var(--line); background-color: #f8f9fa;"></td>
        </tr>
      `
    }
    
    const isAltRow = rowIndex % 2 === 1
    const rowClass = isAltRow ? 'row-alt' : ''
    
    allRows += `
      <tr class="${rowClass}">
        <td>${escapeHtml(claim.id) || '—'}</td>
        <td>${escapeHtml(formatResponsibility(claim.claimResponsibilityType))}</td>
        <td>${escapeHtml(formatServiceDates(claim.fromDos, claim.throughDos))}</td>
        <td>${escapeHtml(formatPatientName(claim.patientFirst, claim.patientLast))}</td>
        <td>${escapeHtml(claim.primaryPayerName) || '—'}</td>
        <td class="right">${claim.chargeAmount ? formatCurrency(claim.chargeAmount.amount, claim.chargeAmount.currency) : '—'}</td>
      </tr>
    `
    
    lastClaimId = currentClaimId
    rowIndex++
  })

  return allRows
}

// Function to build provider information
const buildProviderInfo = (statement: any): string => {
  if (statement?.renderingProviders && statement.renderingProviders.length > 0) {
    return 'Provider(s): ' + statement.renderingProviders
      .filter((provider: any) => provider?.firstName && provider?.lastName)
      .map((provider: any) => `${escapeHtml(provider.firstName)}, ${escapeHtml(provider.lastName)}`)
      .join(', ')
  }
  return ''
}

const buildBillingFacilityColumn = (statement: PatientDetails['statementsList'][0]): string => {
  const fmt = (label: string, value: string | undefined) => {
    const v = value !== undefined && value !== null && String(value).trim() !== '' ? String(value).trim() : '—'
    return `${escapeHtml(label)}: ${escapeHtml(v)}`
  }
  return [
    fmt('Facility', statement?.facility),
    fmt('Billing NPI', statement?.billingNpi),
    fmt('Rendering NPI', statement?.renderingNpi),
    fmt('EIN', statement?.ein)
  ].join('<br/>')
}

const renderDiagnosisTags = (diagnosisCodes: string[] | undefined): string => {
  if (!diagnosisCodes?.length) return ''
  const codes = diagnosisCodes.filter((code) => code != null && String(code).trim() !== '')
  if (!codes.length) return ''
  const tags = codes
    .map((code: string) => `<span class="badge ${getBadgeColor(code)}">${escapeHtml(String(code).trim())}</span>`)
    .join('')
  return `<div style="margin-top: 6px; display: block; line-height: 1.4;"><span style="font-size: 11px; color: #6b7280;">Diagnosis: </span>${tags}</div>`
}

// Function to get badge color for diagnosis code
const getBadgeColor = (code: string): string => {
  // Create a more robust hash from the code to get consistent colors
  if (!code) {
    return 'blue' // default color
  }
  
  let hash = 0
  for (let i = 0; i < code.length; i++) {
    const char = code.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  
  // Add some additional mixing for better distribution
  hash = hash ^ (hash >>> 16)
  hash = hash * 0x85ebca6b
  hash = hash ^ (hash >>> 13)
  hash = hash * 0xc2b2ae35
  hash = hash ^ (hash >>> 16)
  
  // Use absolute value and modulo to get a positive number
  const colorIndex = Math.abs(hash) % 6
  
  const colors = [
    'yellow',    // Color 1
    'green',     // Color 2
    'blue',      // Color 3
    'purple',    // Color 4
    'orange',    // Color 5
    'pink'       // Color 6
  ]
  
  return colors[colorIndex]
}

// Function to generate statements table rows
const generateStatementsTableRows = (statementsList: PatientDetails['statementsList']): string => {
  let allRows = ''
  let rowIndex = 0
  let lastDate = ''

  statementsList.forEach((statement, index) => {
    const currentDate = statement.date
    const isNewDateGroup = currentDate !== lastDate
    const isFirstInGroup = isNewDateGroup
    
    // Add date group separator (except for first group)
    if (isNewDateGroup && index > 0) {
      allRows += `
        <tr class="date-separator">
          <td colspan="6" style="padding: 8px 0; border-top: 2px solid var(--line); background-color: #f8f9fa;"></td>
        </tr>
      `
    }
    
    const isAltRow = rowIndex % 2 === 0
    const rowClass = isAltRow ? 'row-alt' : ''
    
    const billingFacilityCell = buildBillingFacilityColumn(statement)
    const diagnosisTagsHtml = renderDiagnosisTags(statement.diagnosisCodes)

    if (statement.type === 'claim') {
      allRows += `
        <tr class="${rowClass} ${isFirstInGroup ? 'date-group-first' : ''}">
          <td>${formatDate(statement.date)}</td>
          <td>
            <div><strong>${escapeHtml(statement.description) || '—'}</strong> <span class="muted">(${escapeHtml(statement.description)?.includes('Pending') ? 'Pending Adjudication' : ''})</span></div>
            ${diagnosisTagsHtml}
          </td>
          <td>${billingFacilityCell}</td>
          <td class="right">${statement.charge ? formatCurrency(statement.charge) : '—'}</td>
          <td class="right">${statement.credit ? formatCurrency(statement.credit) : '—'}</td>
          <td class="right">${statement.balance ? formatCurrency(statement.balance) : '—'}</td>
        </tr>
      `
    } else if (statement.type === 'line') {
      const providerInfo = buildProviderInfo(statement)
      allRows += `
        <tr class="${rowClass}">
          <td></td>
          <td>
            <div>${escapeHtml(statement.description) || '—'}</div>
            ${providerInfo ? `<div class="muted" style="margin-top: 4px; font-size: 12px;">${escapeHtml(providerInfo)}</div>` : ''}
            ${diagnosisTagsHtml}
          </td>
          <td>${billingFacilityCell}</td>
          <td class="right">${statement.charge ? formatCurrency(statement.charge) : '—'}</td>
          <td class="right">${statement.credit ? formatCurrency(statement.credit) : '—'}</td>
          <td class="right">${statement.balance ? formatCurrency(statement.balance) : '—'}</td>
        </tr>
      `
    } else if (statement.type === 'allocated') {
      const providerInfo = buildProviderInfo(statement)
      allRows += `
        <tr class="${rowClass} ${isFirstInGroup ? 'date-group-first' : ''}">
          <td>${isFirstInGroup ? formatDate(statement.date) : ''}</td>
          <td>
            <div>${escapeHtml(statement.description) || '—'}</div>
            ${providerInfo ? `<div class="muted" style="margin-top: 4px; font-size: 12px;">${escapeHtml(providerInfo)}</div>` : ''}
            ${diagnosisTagsHtml}
          </td>
          <td>${billingFacilityCell}</td>
          <td class="right">${statement.charge ? formatCurrency(statement.charge) : '—'}</td>
          <td class="right">${statement.credit ? formatCurrency(statement.credit) : '—'}</td>
          <td class="right">${statement.balance ? formatCurrency(statement.balance) : '—'}</td>
        </tr>
      `
    } else if (statement.type === 'unallocated') {
      const providerInfo = buildProviderInfo(statement)
      allRows += `
        <tr class="${rowClass} ${isFirstInGroup ? 'date-group-first' : ''}">
          <td>${isFirstInGroup ? formatDate(statement.date) : ''}</td>
          <td>
            <div><strong>${escapeHtml(statement.description) || '—'}</strong></div>
            ${providerInfo ? `<div class="muted" style="margin-top: 4px; font-size: 12px;">${escapeHtml(providerInfo)}</div>` : ''}
            ${diagnosisTagsHtml}
          </td>
          <td>${billingFacilityCell}</td>
          <td class="right">${statement.charge ? formatCurrency(statement.charge) : '—'}</td>
          <td class="right">${statement.credit ? formatCurrency(statement.credit) : '—'}</td>
          <td class="right">${statement.balance ? formatCurrency(statement.balance) : '—'}</td>
        </tr>
      `
    } else if (statement.type === 'adjustment') {
      const providerInfo = buildProviderInfo(statement)
      allRows += `
        <tr class="${rowClass} ${isFirstInGroup ? 'date-group-first' : ''}">
          <td>${isFirstInGroup ? formatDate(statement.date) : ''}</td>
          <td>
            <div><strong>${escapeHtml(statement.description) || '—'}</strong></div>
            ${providerInfo ? `<div class="muted" style="margin-top: 4px; font-size: 12px;">${escapeHtml(providerInfo)}</div>` : ''}
            ${diagnosisTagsHtml}
          </td>
          <td>${billingFacilityCell}</td>
          <td class="right">${statement.charge ? formatCurrency(statement.charge) : '—'}</td>
          <td class="right">${statement.credit ? formatCurrency(statement.credit) : '—'}</td>
          <td class="right">${statement.balance ? formatCurrency(statement.balance) : '—'}</td>
        </tr>
      `
    } else if (statement.type === 'payment') {
      const providerInfo = buildProviderInfo(statement)
      allRows += `
        <tr class="${rowClass} ${isFirstInGroup ? 'date-group-first' : ''}">
          <td>${isFirstInGroup ? formatDate(statement.date) : ''}</td>
          <td>
            <div><strong>${escapeHtml(statement.description) || '—'}</strong></div>
            ${providerInfo ? `<div class="muted" style="margin-top: 4px; font-size: 12px;">${escapeHtml(providerInfo)}</div>` : ''}
            ${diagnosisTagsHtml}
          </td>
          <td>${billingFacilityCell}</td>
          <td class="right">${statement.charge ? formatCurrency(statement.charge) : '—'}</td>
          <td class="right">${statement.credit ? formatCurrency(statement.credit) : '—'}</td>
          <td class="right">${statement.balance ? formatCurrency(statement.balance) : '—'}</td>
        </tr>
      `
    } else if (statement.type === 'balance') {
      const providerInfo = buildProviderInfo(statement)
      allRows += `
        <tr class="${rowClass}">
          <td></td>
          <td>
            <div class="row-note">${escapeHtml(statement.description) || '—'}</div>
            ${providerInfo ? `<div class="muted" style="margin-top: 4px; font-size: 12px;">${escapeHtml(providerInfo)}</div>` : ''}
            ${diagnosisTagsHtml}
          </td>
          <td>${billingFacilityCell}</td>
          <td class="right">${statement.charge ? formatCurrency(statement.charge) : '—'}</td>
          <td class="right">${statement.credit ? formatCurrency(statement.credit) : '—'}</td>
          <td class="right">${statement.balance ? formatCurrency(statement.balance) : '—'}</td>
        </tr>
      `
    }
    
    lastDate = currentDate
    rowIndex++
  })

  return allRows
}

// HTML Template as a string constant
export const TEMPLATE_HTML = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Patient Statement – Enter Health</title>
  <style>
    :root{
      --bg:#f5f7fb;
      --panel:#ffffff;
      --text:#0f172a;
      --muted:#6b7280;
      --line:#e5e7eb;
      --blue:#2563eb;
      --blue-100:#dbeafe;
      --green:#16a34a;
      --yellow:#f59e0b;
      --chip-bg:#f3f4f6;
      --chip-border:#e5e7eb;
    }
    *{box-sizing:border-box}
    body{
      margin:0;
      padding:0;
      background:var(--bg);
      color:var(--text);
      font:14px/1.4 system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji","Segoe UI Emoji";
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .container{max-width:100%;margin:0;padding:0 5px}
    .card{background:var(--panel);border:1px solid var(--line);border-radius:8px;box-shadow:0 1px 2px rgba(0,0,0,.03)}
    .header{
      display:flex;align-items:center;justify-content:space-between;
      padding:12px 16px;border:1px solid var(--line);border-radius:8px;background:#fff;margin-bottom:12px
    }
    .header-left{display:flex;align-items:center;gap:18px}
    .title{font-weight:700;font-size:20px;color:#111827}
    .big-amount{font-size:36px;font-weight:800}
    .actions{display:flex;gap:10px}
    .btn{border:1px solid var(--line);background:#fff;border-radius:8px;padding:10px 14px;font-weight:600;cursor:pointer}
    .btn.primary{background:var(--blue);color:#fff;border-color:var(--blue)}
    .grid-2{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:0;width:100%}
    .panel{padding:12px}
    .panel h3{margin:0 0 10px 0;font-size:14px;text-transform:uppercase;letter-spacing:.04em;color:#111827;font-weight:600}
    .muted{color:var(--muted)}
    .kv{display:grid;grid-template-columns:80px 1fr;row-gap:6px;column-gap:10px}
    .pill{display:inline-flex;align-items:center;gap:8px;background:var(--blue-100);color:var(--blue);border-radius:999px;padding:4px 10px;font-weight:700}
    .mini-btn{font-size:12px;padding:2px 8px;border-radius:6px;border:1px solid var(--line);background:#fff;cursor:pointer}
    .tiny{font-size:12px}
    .table{width:100%;border-collapse:separate;border-spacing:0;margin-top:8px}
    .table th, .table td{padding:10px 12px;border-top:1px solid var(--line);vertical-align:top}
    .table th{background:#fafafa;font-weight:700;color:#374151;text-align:left}
    .table tr:first-child th{border-top:none}
    .table tr:first-child td{border-top:none}
    .table .right{text-align:right;white-space:nowrap}
    .row-note{font-size:12px;color:var(--muted)}
        .badge{display:inline-block;padding:2px 8px;border-radius:6px;background:#fff;border:1px solid var(--chip-border);margin-right:6px;margin-left:8px}
        .badge.yellow{background:#FEF3C7;border-color:#FDE68A}
        .badge.green{background:#DCFCE7;border-color:#86EFAC}
        .badge.blue{background:#DBEAFE;border-color:#93C5FD}
        .badge.purple{background:#E9D5FF;border-color:#C4B5FD}
        .badge.orange{background:#FED7AA;border-color:#FDBA74}
        .badge.pink{background:#FCE7F3;border-color:#F9A8D4}
        .date-separator{background-color:#f8f9fa !important}
        .date-group-first{border-top:1px solid var(--line)}
    .section-title{padding:14px 16px;border-bottom:1px solid var(--line);font-weight:700}
    .toolbar{padding:10px 16px;border-top:1px solid var(--line);display:flex;justify-content:flex-end;gap:8px}
    .shadowed{box-shadow:inset 0 1px 0 #fff}
    .subtle{color:#111827}
    .row-alt{background:#f9fbff}
    .grid-2 > *{min-width:0;overflow:hidden}
    @media (max-width:600px){
      .container{padding:0 8px}
      .grid-2{grid-template-columns:1fr;gap:12px}
      .kv{grid-template-columns:80px 1fr}
      .header{flex-direction:column;align-items:flex-start;gap:8px;padding:12px 16px;margin-bottom:12px}
      .actions{align-self:flex-end}
      .panel{padding:12px}
    }
    @media print {
      .container{padding:0 2px}
      .card{box-shadow:none;border:1px solid #ddd;margin-bottom:3px;page-break-inside:avoid;orphans:3;widows:3}
      .btn{display:none}
      .header{padding:6px 10px;margin-bottom:6px;page-break-inside:avoid;page-break-after:avoid}
      .panel{padding:6px;page-break-inside:avoid}
      .grid-2{display:grid !important;grid-template-columns:1fr 1fr !important;gap:8px;page-break-inside:avoid;width:100%;orphans:3;widows:3}
      .kv{grid-template-columns:70px 1fr;row-gap:4px;column-gap:8px}
      .section-title{padding:6px 12px;margin-bottom:0;page-break-after:avoid}
      .table{page-break-inside:auto;margin-top:0;break-inside:auto}
      .table th, .table td{padding:4px 8px;page-break-inside:avoid}
      .table tr{page-break-inside:avoid;page-break-after:auto;orphans:2;widows:2}
      .table thead{display:table-header-group}
      .table tbody tr:first-child{page-break-before:avoid}
      .card + .card{margin-top:6px !important}
      .badge{page-break-inside:avoid}
      .row-note{page-break-inside:avoid}
    }
  .page-break-before{page-break-before: always; break-before: page;}
    </style>
</head>
<body>
  <div class="container">
    <!-- Top header -->
    <div class="header card">
      <div class="header-left">
        <div>
          <div class="title">Current Balance</div>
          <div class="big-amount">{{BALANCE_AMOUNT}}</div>
        </div>
      </div>
    </div>
    <!-- Patient / Billing provider -->
    <div class="grid-2" style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; width: 100%;">
      <div class="card panel" style="min-width: 0; overflow: hidden;">
        <h3>Patient</h3>
        <div class="kv">
          <div>Name</div><div><strong>{{PATIENT_NAME}}</strong> <span class="badge">{{PATIENT_SEX}}</span></div>
          <div>DOB</div><div>{{PATIENT_DOB}}</div>
          <div>Phone</div><div>{{PATIENT_PHONE}}</div>
          <div>Email</div><div><a href="mailto:{{PATIENT_EMAIL}}">{{PATIENT_EMAIL}}</a></div>
          <div>Address</div><div>{{PATIENT_ADDRESS}}</div>
          <div>External ID</div>
          <div>
            {{PATIENT_EXTERNAL_ID}}
          </div>
        </div>
      </div>
      <div class="card panel" style="min-width: 0; overflow: hidden;">
        <h3>Billing Provider</h3>
        <div class="kv">
          <div>Organization</div><div><strong>{{BILLING_ORGANIZATION}}</strong></div>
          <div>Phone</div><div>{{BILLING_PHONE}}</div>
          <div>Email</div><div><a href="mailto:{{BILLING_EMAIL}}">{{BILLING_EMAIL}}</a></div>
          <div>Address</div><div>{{BILLING_ADDRESS}}</div>
          <div>EIN</div><div>{{BILLING_EIN}}</div>
          <div>NPI</div><div>{{BILLING_NPI}}</div>

        </div>
      </div>
    </div>
    <!-- Claims -->
    <div class="card" style="margin-top:8px;">
      <div class="section-title">Claims</div>
      <div class="panel">
        <table class="table">
          <thead>
            <tr>
              <th>Claim ID</th>
              <th>Responsibility</th>
              <th>Service Dates</th>
              <th>Patient Name</th>
              <th>Payer</th>
              <th class="right">Total Charge</th>
            </tr>
          </thead>
          <tbody>
            {{CLAIMS_TABLE_ROWS}}
          </tbody>
        </table>
      </div>
    </div>
    <!-- Payments -->
    <div class="card" style="margin-top:8px;">
      <div class="section-title">Payments</div>
      <div class="panel">
        <table class="table">
          <thead>
            <tr>
              <th>Payment date</th>
              <th class="right">Total amount</th>
              <th class="right">Unallocated amount</th>
              <th class="right">Allocated amount</th>
              <th class="right">Refund amount</th>
            </tr>
          </thead>
          <tbody>
            {{PAYMENTS_TABLE_ROWS}}
          </tbody>
        </table>
      </div>
    </div>
    {{STATEMENT_SECTION}}
  </div>
</body>
</html>`

// Data transformation function to convert from React component data structures to PDF format
export const transformDataForPDF = (
  patientStatement: any,
  paymentsData: any[],
  statementData: any[],
  claimsData?: any[]
): PatientDetails => {
  return {
    statement: patientStatement,
    paymentsList: (paymentsData || []).map(payment => ({
      id: payment?.id || '',
      totalAmount: payment?.totalAmount || { amount: '0', currency: 'USD' },
      unallocatedAmount: payment?.unallocatedAmount || { amount: '0', currency: 'USD' },
      allocatedAmount: payment?.allocatedAmount || { amount: '0', currency: 'USD' },
      externalTransactionId: payment?.externalTransactionId || '',
      paymentDate: payment?.paymentDate || '',
      refundAmount: payment?.refundAmount || { amount: '0', currency: 'USD' }
    })),
    statementsList: (statementData || []).map(statement => {
      // Find the claim to get rendering providers and facility/npi/ein
      const claim = patientStatement?.claims?.find((c: any) => c?.id === statement?.claimId);
      const billingProvider = patientStatement?.billingProvider;
      const facilityName = claim?.facility?.name ?? (typeof claim?.facility === 'string' ? claim?.facility : undefined);
      const renderingNpis = (claim?.renderingProviders || [])
        .map((p: any) => p?.npi)
        .filter(Boolean);
      const renderingNpi = renderingNpis.length > 0 ? renderingNpis.join(', ') : undefined;

      return {
        id: statement?.id || '',
        type: statement?.type || '',
        date: statement?.date || '',
        description: statement?.description || '',
        charge: statement?.charge?.amount || '0',
        credit: statement?.credit?.amount || '0',
        balance: statement?.balance?.amount || '0',
        diagnosisCodes: statement?.diagnosisCodes || [],
        renderingProviders: claim?.renderingProviders || [],
        facility: facilityName,
        billingNpi: claim?.billingNpi ?? claim?.npi ?? billingProvider?.npi,
        renderingNpi,
        ein: claim?.ein ?? billingProvider?.ein,
        metadata: statement?.metadata
      };
    }),
    claimsList: (claimsData || []).map(claim => ({
      id: claim?.id || '',
      claimResponsibilityType: claim?.claimResponsibilityType || undefined,
      fromDos: claim?.fromDos || undefined,
      throughDos: claim?.throughDos || undefined,
      patientFirst: claim?.patientFirst || undefined,
      patientLast: claim?.patientLast || undefined,
      primaryPayerName: claim?.primaryPayerName || undefined,
      chargeAmount: claim?.chargeAmount || undefined
    }))
  };
};


export type StatementTotals = { totalCharge: number; totalCredit: number; totalBalance: number };

const parseMoney = (val: any): number => {
  if (val === null || val === undefined) return 0;
  if (typeof val === 'number') return val;
  const s = String(val).trim();
  if (!s || s === '—') return 0;
  const num = parseFloat(s.replace(/[$,]/g, ''));
  return Number.isFinite(num) ? num : 0;
};

export const computeStatementTotals = (statementsList: PatientDetails["statementsList"]): StatementTotals => {
  let totalCharge = 0;
  let totalCredit = 0;
  let totalBalance = 0;

  for (const row of statementsList || []) {
    totalCharge += parseMoney((row as any).charge);
    totalCredit += parseMoney((row as any).credit);
    totalBalance += parseMoney((row as any).balance);
  }

  return { totalCharge, totalCredit, totalBalance };
};

const renderStatementTable = (
  rowsHtml: string,
  title: string,
  opts: { showTotalRow: boolean; totals?: StatementTotals },
): string => {
  const { showTotalRow, totals } = opts;
  const headerHtml = title
    ? `<div class="section-title">${escapeHtml(title)}</div>`
    : "";

  const totalRow =
    showTotalRow && totals
      ? `
      <tr style="border-top: 2px solid var(--line); background-color: #f8f9fa; font-weight: 700;">
        <td colspan="3" style="text-align: right; padding: 12px 8px;">Total:</td>
        <td class="right" style="padding: 12px 8px;">${formatCurrency(totals.totalCharge.toString())}</td>
        <td class="right" style="padding: 12px 8px;">${formatCurrency(totals.totalCredit.toString())}</td>
        <td class="right" style="padding: 12px 8px;">${formatCurrency(totals.totalBalance.toString())}</td>
      </tr>
    `
      : "";

  return `
    <div class="card" style="margin-top:8px;">
      ${headerHtml}
      <div class="panel">
        <table class="table">
          <thead>
            <tr>
              <th style="width:120px">Date</th>
              <th>Description</th>
              <th style="width:240px">Billing & facility</th>
              <th class="right" style="width:120px">Charge</th>
              <th class="right" style="width:120px">Credit</th>
              <th class="right" style="width:120px">Balance</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
            ${totalRow}
          </tbody>
        </table>
      </div>
    </div>
  `;
};

// Function to generate HTML from patient details (single-table version, kept for compatibility)
export const generateHTML = (data: PatientDetails, template: string): string => {
  const { statementsList } = data
  const totals = computeStatementTotals(statementsList || [])
  return generateHTMLForStatementChunk(data, template, {
    showTotalRow: true,
    totals,
    showTitle: true,
  })
}

export const generateHTMLForStatementChunk = (
  data: PatientDetails,
  template: string,
  opts: { showTotalRow: boolean; totals: StatementTotals; showTitle?: boolean },
): string => {
  const { statement, paymentsList, statementsList, claimsList } = data
  const { patient, billingProvider, balanceDue } = statement || {}

  const patientAddress = patient?.addresses?.length ? patient.addresses[0] : null
  const billingAddress = billingProvider?.addresses?.length ? billingProvider.addresses[0] : null

  const statementTitle = opts.showTitle === false ? '' : 'Statement'
  const statementSection = renderStatementTable(
    generateStatementsTableRows(statementsList || []),
    statementTitle,
    { showTotalRow: opts.showTotalRow, totals: opts.totals },
  )

  const html = template
    .replaceAll('{{BALANCE_AMOUNT}}', formatCurrency(balanceDue?.amount || '0', balanceDue?.currency || 'USD'))
    .replaceAll('{{PATIENT_NAME}}', escapeHtml(`${patient?.first || ''} ${patient?.middle || ''} ${patient?.last || ''}`.trim()) || '—')
    .replaceAll('{{PATIENT_SEX}}', escapeHtml(patient?.sex) || '—')
    .replaceAll('{{PATIENT_DOB}}', formatDate(patient?.dob || ''))
    .replaceAll('{{PATIENT_PHONE}}', escapeHtml(patient?.phones?.[0]) || '—')
    .replaceAll('{{PATIENT_EMAIL}}', escapeHtml(patient?.emails?.[0]) || '—')
    .replaceAll('{{PATIENT_EXTERNAL_ID}}', escapeHtml(patient?.externalId) || '—')
    .replaceAll('{{PATIENT_ADDRESS}}', patientAddress ? 
      `${(patientAddress.lines || []).map(line => escapeHtml(line)).join('<br/>')}<br/>${escapeHtml(patientAddress.city || '')}, ${escapeHtml(patientAddress.state || '')} ${escapeHtml(patientAddress.postal || '')} ${escapeHtml(patientAddress.country || '')}` : 
      '—'
    )
    .replaceAll('{{BILLING_ORGANIZATION}}', escapeHtml(billingProvider?.organization) || '—')
    .replaceAll('{{BILLING_PHONE}}', escapeHtml(billingProvider?.phone) || '—')
    .replaceAll('{{BILLING_EMAIL}}', escapeHtml(billingProvider?.email) || '—')
    .replaceAll('{{BILLING_ADDRESS}}', billingAddress ? 
      `${(billingAddress.lines || []).map(line => escapeHtml(line)).join('<br/>')}<br/>${escapeHtml(billingAddress.city || '')}, ${escapeHtml(billingAddress.state || '')} ${escapeHtml(billingAddress.postal || '')} ${escapeHtml(billingAddress.country || '')}` : 
      '—'
    )
    .replaceAll('{{BILLING_EIN}}', escapeHtml(billingProvider?.ein) || '—')
    .replaceAll('{{BILLING_NPI}}', escapeHtml(billingProvider?.npi) || '—')
    .replaceAll('{{PAYMENTS_TABLE_ROWS}}', generatePaymentsTableRows(paymentsList || []))
    .replaceAll('{{CLAIMS_TABLE_ROWS}}', generateClaimsTableRows(claimsList || []))
    .replaceAll('{{STATEMENT_SECTION}}', statementSection)

  return html
}